import type { AddressFormProps } from '@/components/Patient/forms/AddressForm/AddressForm';
import { AddressForm } from '@/components/Patient/forms/AddressForm/AddressForm';
import type { ConcessionCardProps } from '@/components/Patient/forms/ConcessionCard/ConcessionCard';
import { ConcessionCard } from '@/components/Patient/forms/ConcessionCard/ConcessionCard';
import type { ContactDetailsProps } from '@/components/Patient/forms/ContactDetails/ContactDetails';
import { ContactDetails } from '@/components/Patient/forms/ContactDetails/ContactDetails';
import type { EmergencyContactProps } from '@/components/Patient/forms/EmergencyContact/EmergencyContact';
import { EmergencyContact } from '@/components/Patient/forms/EmergencyContact/EmergencyContact';
import type { IhiInformationProps } from '@/components/Patient/forms/IhiInformation/IhiInformation';
import { IhiInformation } from '@/components/Patient/forms/IhiInformation/IhiInformation';
import type { MedicareProps } from '@/components/Patient/forms/Medicare/Medicare';
import { Medicare } from '@/components/Patient/forms/Medicare/Medicare';
import type { PatientInformationProps } from '@/components/Patient/forms/PatientInformation/PatientInformation';
import { PatientInformation } from '@/components/Patient/forms/PatientInformation/PatientInformation';
import type { TreatingDoctorProps } from '@/components/Patient/forms/TreatingDoctor/TreatingDoctor';
import { TreatingDoctor } from '@/components/Patient/forms/TreatingDoctor/TreatingDoctor';
import type { Role } from '@/data/service/authService';
import Grid from '@mui/material/Grid';

export const renderMedicareForm = (props: MedicareProps): React.ReactElement => {
  return (
    <Grid item xs={12} lg={6}>
      <Medicare {...props} />
    </Grid>
  );
};

export const renderIhiForm = (props: IhiInformationProps): React.ReactElement => {
  return (
    <Grid item xs={12} lg={6}>
      <IhiInformation {...props} />
    </Grid>
  );
};

export const renderPatientInformationForm = (props: PatientInformationProps): React.ReactElement => {
  return (
    <Grid item xs={12}>
      <PatientInformation {...props} />
    </Grid>
  );
};

export const renderContactDetailsForm = (props: ContactDetailsProps): React.ReactElement => {
  return (
    <Grid item xs={12}>
      <ContactDetails {...props} />
    </Grid>
  );
};

export const renderEmergencyContactForm = (props: EmergencyContactProps): React.ReactElement => {
  return (
    <Grid item xs={12}>
      <EmergencyContact {...props} />
    </Grid>
  );
};

export const renderAddressForm = (props: AddressFormProps): React.ReactElement => {
  return (
    <Grid item xs={12}>
      <AddressForm {...props} />
    </Grid>
  );
};

export const renderTreatingDoctorForm = (props: TreatingDoctorProps): React.ReactElement => {
  return (
    <Grid item xs={12}>
      <TreatingDoctor {...props} />
    </Grid>
  );
};

export const renderConcessionCardForm = (
  props: ConcessionCardProps & { hasConcessionCard: boolean }
): React.ReactElement | null => {
  const { hasConcessionCard, ...concessionCardProps } = props;

  if (!hasConcessionCard) {
    return null;
  }

  return (
    <Grid item xs={12}>
      <ConcessionCard {...concessionCardProps} />
    </Grid>
  );
};

export const getOrderedFormsByRole = (
  role: Role | null,
  components: {
    medicareForm: React.ReactElement;
    ihiForm: React.ReactElement;
    patientInformationForm: React.ReactElement;
    contactDetailsForm: React.ReactElement;
    emergencyContactForm: React.ReactElement;
    addressForm: React.ReactElement;
    treatingDoctorForm: React.ReactElement;
    concessionCardForm: React.ReactElement | null;
  }
) => {
  switch (role) {
    case 'superAdmin':
    case 'ptxSupportPartner':
    case 'admin':
      return [
        components.patientInformationForm,
        components.contactDetailsForm,
        components.addressForm,
        components.medicareForm,
        components.ihiForm,
        components.emergencyContactForm,
        components.treatingDoctorForm,
        components.concessionCardForm
      ].filter(Boolean);
    case 'adminOnly':
      return [
        components.patientInformationForm,
        components.contactDetailsForm,
        components.addressForm,
        components.medicareForm,
        components.ihiForm,
        components.emergencyContactForm,
        components.concessionCardForm
      ].filter(Boolean);
    default:
      return [
        components.medicareForm,
        components.ihiForm,
        components.patientInformationForm,
        components.contactDetailsForm,
        components.emergencyContactForm,
        components.addressForm,
        components.treatingDoctorForm,
        components.concessionCardForm
      ].filter(Boolean);
  }
};
